import { apiClient } from '@/lib/api/client'
import { ApiError } from '@/lib/api/client'
import { config } from '@/lib/config'
import type {
  AlignQuoteVersionVatRequestDto,
  AlignQuoteVersionVatResponseDto,
  LineRequestDto,
  LineResponseDto,
  ProjectQuoteCategoriesResponseDto,
  ProjectSalesVatsDto,
  ProjectQuotesV2Response,
  QuoteDraftVersion,
  QuoteVersionDefaultsDto,
  QuoteVersionDefaultsRequestDto,
  QuoteV2,
  QuoteVersionPdfUrlDto,
  QuoteVersionSelectionDto,
} from './types'

export async function getProjectQuotesV2(projectId: string): Promise<ProjectQuotesV2Response> {
  return await apiClient.get<ProjectQuotesV2Response>(`/v2/projects/${projectId}/quotes`)
}

export async function getProjectQuoteCategoriesV2(projectId: string): Promise<ProjectQuoteCategoriesResponseDto> {
  return await apiClient.get<ProjectQuoteCategoriesResponseDto>(`/v2/projects/${projectId}/sales/categories`)
}

export async function getProjectSalesVatsV2(projectId: string): Promise<ProjectSalesVatsDto> {
  return await apiClient.get<ProjectSalesVatsDto>(`/v2/projects/${projectId}/sales/vats`)
}

export async function createProjectQuoteDraftV2(
  projectId: string,
  body?: {
    name?: string | null
  }
): Promise<QuoteV2> {
  return await apiClient.post<QuoteV2>(`/v2/projects/${projectId}/quotes`, body)
}

export async function getProjectQuoteVersionPdfUrlV2(
  projectId: string,
  quoteId: string,
  versionId: string
): Promise<QuoteVersionPdfUrlDto> {
  return await apiClient.get<QuoteVersionPdfUrlDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/pdf-url`
  )
}

export async function getQuoteDraftDetailsV2(
  projectId: string,
  quoteId: string,
  versionId: string
): Promise<QuoteDraftVersion> {
  return await apiClient.get<QuoteDraftVersion>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/draft-details`
  )
}

export async function getQuoteVersionPreviewPdfV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  opts?: { language?: string }
): Promise<Blob> {
  const qs = new URLSearchParams()
  if (opts?.language) qs.set('language', opts.language)
  const query = qs.toString()

  const url = `${config.apiBaseUrl}/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/preview-pdf${
    query ? `?${query}` : ''
  }`

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/pdf',
    },
  })

  if (response.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', 'Non authentifié')
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'QUOTE_PREVIEW_PDF_FAILED', "Impossible de charger l'aperçu")
  }

  return await response.blob()
}

export async function generateQuoteVersionDocumentV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  request: {
    validUntil: string
  }
): Promise<void> {
  await apiClient.post(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/generate-document`,
    request
  )
}

export async function duplicateQuoteVersionV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  body?: { name: string }
): Promise<void> {
  await apiClient.post(`/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/duplicate`, body)
}

export async function deleteQuoteVersionV2(projectId: string, quoteId: string, versionId: string): Promise<void> {
  await apiClient.delete(`/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}`)
}

export async function declineQuoteVersionV2(projectId: string, quoteId: string, versionId: string): Promise<void> {
  await apiClient.post(`/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/decline`)
}

export async function addQuoteVersionLineV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  request: LineRequestDto
): Promise<LineResponseDto> {
  return await apiClient.post<LineResponseDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/lines`,
    request
  )
}

export async function updateQuoteVersionLineV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  lineId: string,
  request: LineRequestDto
): Promise<LineResponseDto> {
  return await apiClient.put<LineResponseDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/lines/${lineId}`,
    request
  )
}

export async function getQuoteVersionLineV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  lineId: string
): Promise<LineResponseDto> {
  return await apiClient.get<LineResponseDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/lines/${lineId}`
  )
}

export async function moveQuoteVersionLineV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  lineId: string,
  newDisplayOrder: number
): Promise<LineResponseDto> {
  return await apiClient.post<LineResponseDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/lines/${lineId}/move`,
    { newDisplayOrder }
  )
}

export async function deleteQuoteVersionLineV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  lineId: string
): Promise<void> {
  await apiClient.delete(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/lines/${lineId}`
  )
}

export async function getQuoteVersionDefaultAttributesV2(
  projectId: string,
  quoteId: string,
  versionId: string
): Promise<QuoteVersionDefaultsDto> {
  return await apiClient.get<QuoteVersionDefaultsDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/config/default-attributes`
  )
}

export async function putQuoteVersionDefaultAttributesV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  body: QuoteVersionDefaultsRequestDto
): Promise<void> {
  await apiClient.put(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/config/default-attributes`,
    body
  )
}

export async function acceptQuoteVersionV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  req: { customerAcceptedDate?: string | null }
): Promise<void> {
  await apiClient.post(`/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/accept`, req)
}

export async function generateWorkOrderPdfFromQuotes(
  projectId: string,
  selections: QuoteVersionSelectionDto[],
  opts?: { language?: string }
): Promise<Blob> {
  const qs = new URLSearchParams()
  if (opts?.language) qs.set('language', opts.language)
  const query = qs.toString()

  const url = `${config.apiBaseUrl}/projects/${projectId}/work-order/pdf-from-quotes${
    query ? `?${query}` : ''
  }`

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    body: JSON.stringify({ selections }),
  })

  if (response.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', 'Non authentifié')
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      'WORK_ORDER_PDF_FAILED',
      'Impossible de générer le bordereau'
    )
  }

  return await response.blob()
}

export async function alignQuoteVersionVatV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  request: AlignQuoteVersionVatRequestDto
): Promise<AlignQuoteVersionVatResponseDto> {
  return await apiClient.post<AlignQuoteVersionVatResponseDto>(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/align-vat`,
    request
  )
}

export async function putQuoteVersionWorkaroundNoteV2(
  projectId: string,
  quoteId: string,
  versionId: string,
  request: {
    workaroundRawNote: string | null
  }
): Promise<void> {
  await apiClient.put(
    `/v2/projects/${projectId}/quotes/${quoteId}/versions/${versionId}/config/workaround-note`,
    request
  )
}
